import React, { PropsWithChildren } from "react";

import {
  Container,
  type ContainerProps,
} from "@brexhq/next/src/components/layout/Container/Container";
import { type MediaFillLocationType } from "@brexhq/next/src/components/modules/CardsModule/types";
import {
  IBynderImage,
  IBynderMediaStyle,
} from "@brexhq/shared/types/BynderAsset";

import { LegacyImage } from "./LegacyImage";
import { NextImage } from "./NextImage";

export type BynderImageProps = {
  image: IBynderImage;
  maxWidth?: number;
  altTextOverride?: string;
  className?: string;
  loading?: "eager" | "lazy";

  /**
   * @deprecated Avoid using this setting for new components
   */
  useLegacyImage?: boolean;
};

/**
 * @param image - Bynder Asset
 * @param maxWidth - max width of asset
 * @param altTextOverride - optional alt text - if not supplied it will generate from Bynder description
 * @param useLegacyImage - use legacy image redering instead of next/image (should not be used for new components)
 */
export const BynderImage: React.FC<BynderImageProps> = ({
  useLegacyImage,
  image,
  maxWidth,
  altTextOverride,
  className,
  loading,
}) => {
  const shouldUseLegacyImage =
    image?.useLegacyImage !== undefined && image?.useLegacyImage !== null
      ? image.useLegacyImage
      : useLegacyImage ?? true;
  return shouldUseLegacyImage ? (
    <LegacyImage
      image={image}
      maxWidth={maxWidth}
      altTextOverride={altTextOverride}
      className={className}
      loading={loading}
    />
  ) : (
    <NextImage
      image={image}
      width={maxWidth}
      altTextOverride={altTextOverride}
      className={className}
      loading={loading}
      priority={image.priority}
      responsiveLayout={image.responsiveLayout}
    />
  );
};

export const ImageBorderRadiusWrapper: React.FC<
  PropsWithChildren<
    {
      mediaStyle: IBynderMediaStyle | null;
    } & ContainerProps
  >
> = ({ children, mediaStyle, ...props }) => {
  const { borderRadius = "0px", customBorderRadius } = mediaStyle ?? {};
  return borderRadius !== "0px" && borderRadius ? (
    <Container
      borderRadius={
        borderRadius !== "Custom" ? borderRadius : customBorderRadius + "px"
      }
      overflowX="hidden"
      overflowY="hidden"
      display="flex"
      width="100%"
      {...props}
    >
      {children}
    </Container>
  ) : (
    <>{children}</>
  );
};

export const ContainedImageWrapper: React.FC<
  PropsWithChildren<
    {
      mediaFillLocation?: MediaFillLocationType;
    } & ContainerProps
  >
> = ({ children, mediaFillLocation, ...props }) => {
  let alignItems = "center";
  let justifyContent = "center";

  switch (mediaFillLocation) {
    case "top":
      alignItems = "flex-end";
      break;
    case "bottom":
      alignItems = "flex-start";
      break;
    case "left":
      justifyContent = "flex-end";
      break;
    case "right":
      justifyContent = "flex-start";
      break;
  }

  return (
    <Container
      overflowX="hidden"
      overflowY="hidden"
      display="flex"
      justifyContent={justifyContent}
      alignItems={alignItems}
      width="auto"
      height="auto"
      maxHeight="100%"
      maxWidth="100%"
      {...props}
    >
      {children}
    </Container>
  );
};
